/**
 * Proposal negotiation helpers.
 *
 * A proposal is a back-and-forth: the team that made the latest offer waits, and
 * the OTHER team responds (accept / decline / counter). With no counters the
 * proposing team made the offer and the opponent responds; after a counter the
 * turn flips. These pure helpers are shared by the proposals list (Games page)
 * and the proposal detail page so "whose turn is it" is derived one way.
 */
import type { GameProposal, TimeSlot } from '@sulis-liiga/common';

/** The team whose turn it is to respond (accept / decline / counter). */
export function respondingTeamId(proposal: GameProposal): string {
  const counters = proposal.counterProposals;
  const lastOfferTeamId = counters.length
    ? counters[counters.length - 1].teamId
    : proposal.proposingTeamId;
  return lastOfferTeamId === proposal.proposingTeamId
    ? proposal.opposingTeamId
    : proposal.proposingTeamId;
}

/**
 * The live slots to act on — the latest counter's slots if the proposal has been
 * countered, otherwise the original proposed slots.
 */
export function activeSlots(proposal: GameProposal): TimeSlot[] {
  const counters = proposal.counterProposals;
  return counters.length ? counters[counters.length - 1].slots : proposal.proposedSlots;
}

/** Whether one of the viewer's teams is the team that must respond next. */
export function canRespondToProposal(proposal: GameProposal, myTeamIds: Set<string>): boolean {
  return myTeamIds.has(respondingTeamId(proposal));
}
